import { ApiQueryName } from "@/models/api"
import type { Organization } from "@/models/organization"
import {
  BILL_PDF_UPLOAD_SESSION_PATH as SESSION_PATH,
  requestBillPdfUploadSession,
} from "@/modules/data/utils/bill-pdf-upload-session-request"
import { useRenameQueryResult } from "@/services/utils"
import { useQueryKey } from "@/services/utils/useQueryKey"
import type { HttpError, HttpErrorRecord } from "@/utils/api"
import { httpClient } from "@/utils/api"
import { getStringValue } from "@/utils/value"
import { useQuery } from "@tanstack/react-query"
import type { UseQueryOptions } from "@tanstack/react-query"

import type {
  BillPdfAnalysisRecord,
  BillPdfCostTotal,
  BillPdfUploadSessionAnalysis,
  BillPdfUsageTotal,
} from "./bill-pdf-upload-session-analysis.types"

type CountValue = number | string | null

interface CoveragePeriodBound {
  end?: string
  index?: number
  parseId?: number
  start?: string
}

interface CoverageDiscontinuity {
  after?: CoveragePeriodBound
  before?: CoveragePeriodBound
  days?: number
  kind?: string
}

interface CoverageMeter {
  costCurrencies?: string[]
  currency?: string
  discontinuities?: CoverageDiscontinuity[]
  key?: string
  keyKind?: string
  totalCost?: CountValue
  totalUsage?: CountValue
  usageUnits?: string[]
}

interface CoverageReport {
  bills?: CountValue
  meters: CoverageMeter[]
  notReady?: { parseId?: number; state?: string }[]
}

interface AnalysisAmountTotal {
  amount?: CountValue
  currency?: string
  unit?: string
}

// Core returns either a per-meter coverage report or an already summarized
// analysis payload, so both shapes are mapped onto the same view model.
interface AnalysisSummary {
  billsCount?: CountValue
  costTotals?: AnalysisAmountTotal[]
  errorsCount?: CountValue
  gaps?: BillPdfAnalysisRecord[]
  gapsCount?: CountValue
  overlaps?: BillPdfAnalysisRecord[]
  overlapsCount?: CountValue
  usageTotals?: AnalysisAmountTotal[]
}

interface AnalysisResponse {
  data?: CoverageReport | AnalysisSummary | null
}

const toCount = (value: CountValue | undefined): number | null => {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value
  }

  if (typeof value !== "string" || value.trim() === "") {
    return null
  }

  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : null
}

const toDisplayAmount = (value: CountValue | undefined): string =>
  getStringValue(value) ?? ""

const mapUsageTotals = (
  totals: AnalysisAmountTotal[] | undefined
): BillPdfUsageTotal[] =>
  (totals ?? []).map((total) => ({
    amount: toDisplayAmount(total.amount),
    unit: total.unit ?? "",
  }))

const mapCostTotals = (
  totals: AnalysisAmountTotal[] | undefined
): BillPdfCostTotal[] =>
  (totals ?? []).map((total) => ({
    amount: toDisplayAmount(total.amount),
    currency: total.currency ?? "",
  }))

const isCoverageReport = (
  value: CoverageReport | AnalysisSummary
): value is CoverageReport => "meters" in value && Array.isArray(value.meters)

const getMeterUnit = (meter: CoverageMeter): string =>
  meter.usageUnits?.[0] ?? ""

const getMeterCurrency = (meter: CoverageMeter): string => {
  if (meter.currency != null && meter.currency.trim() !== "") {
    return meter.currency
  }

  return meter.costCurrencies?.[0] ?? ""
}

const getUsageTotalsFromMeters = (
  meters: CoverageMeter[]
): BillPdfUsageTotal[] =>
  meters.flatMap((meter) => {
    const amount = toDisplayAmount(meter.totalUsage)
    if (amount === "") {
      return []
    }

    return [{ amount, unit: getMeterUnit(meter) }]
  })

const getCostTotalsFromMeters = (meters: CoverageMeter[]): BillPdfCostTotal[] =>
  meters.flatMap((meter) => {
    const amount = toDisplayAmount(meter.totalCost)
    if (amount === "") {
      return []
    }

    return [{ amount, currency: getMeterCurrency(meter) }]
  })

const getDiscontinuityRecords = (
  meters: CoverageMeter[],
  kind: "gap" | "overlap"
): BillPdfAnalysisRecord[] =>
  meters.flatMap((meter) =>
    (meter.discontinuities ?? [])
      .filter((discontinuity) => discontinuity.kind === kind)
      .map((discontinuity) => ({
        afterEnd: discontinuity.after?.end,
        afterIndex: discontinuity.after?.index,
        afterParseId: discontinuity.after?.parseId,
        beforeIndex: discontinuity.before?.index,
        beforeParseId: discontinuity.before?.parseId,
        beforeStart: discontinuity.before?.start,
        days: discontinuity.days,
        key: meter.key,
        keyKind: meter.keyKind,
        kind: discontinuity.kind,
      }))
  )

const buildEmptyAnalysis = (): BillPdfUploadSessionAnalysis => ({
  billsCount: null,
  costTotals: [],
  errorsCount: null,
  gaps: [],
  gapsCount: null,
  overlaps: [],
  overlapsCount: null,
  usageTotals: [],
})

const mapCoverageReport = (
  value: CoverageReport
): BillPdfUploadSessionAnalysis => {
  const meters = value.meters
  const gaps = getDiscontinuityRecords(meters, "gap")
  const overlaps = getDiscontinuityRecords(meters, "overlap")

  return {
    billsCount: toCount(value.bills) ?? 0,
    costTotals: getCostTotalsFromMeters(meters),
    errorsCount: (value.notReady ?? []).length,
    gaps,
    gapsCount: gaps.length,
    overlaps,
    overlapsCount: overlaps.length,
    usageTotals: getUsageTotalsFromMeters(meters),
  }
}

const mapAnalysisSummary = (
  value: AnalysisSummary
): BillPdfUploadSessionAnalysis => ({
  billsCount: toCount(value.billsCount),
  costTotals: mapCostTotals(value.costTotals),
  errorsCount: toCount(value.errorsCount),
  gaps: value.gaps ?? [],
  gapsCount: toCount(value.gapsCount),
  overlaps: value.overlaps ?? [],
  overlapsCount: toCount(value.overlapsCount),
  usageTotals: mapUsageTotals(value.usageTotals),
})

const mapAnalysis = (
  value: CoverageReport | AnalysisSummary | null | undefined
): BillPdfUploadSessionAnalysis => {
  if (value == null) {
    return buildEmptyAnalysis()
  }

  if (isCoverageReport(value)) {
    return mapCoverageReport(value)
  }

  return mapAnalysisSummary(value)
}

const fetchSessionAnalysis = async ({
  organizationId,
  sessionId,
}: {
  organizationId: Organization["id"]
  sessionId: string
}): Promise<BillPdfUploadSessionAnalysis> =>
  requestBillPdfUploadSession(async () => {
    const response = await httpClient.get<AnalysisResponse>(
      `${SESSION_PATH}/${sessionId}/analysis`,
      { params: { organization_id: organizationId } }
    )
    return mapAnalysis(response.data.data)
  })

interface UseBillPdfUploadSessionAnalysisOptions {
  enabled?: boolean
  organizationId?: Organization["id"]
  queryOptions?: Omit<
    UseQueryOptions<BillPdfUploadSessionAnalysis, HttpError<HttpErrorRecord>>,
    "queryFn" | "queryKey"
  >
  sessionId?: string
}

export const useBillPdfUploadSessionAnalysis = ({
  enabled = true,
  organizationId,
  queryOptions = {},
  sessionId,
}: UseBillPdfUploadSessionAnalysisOptions = {}) => {
  const queryKey = useQueryKey(
    ApiQueryName.BillPdfUploadSessionAnalysis,
    "getOne",
    sessionId,
    { organizationId }
  )

  const queryResult = useQuery({
    ...queryOptions,
    enabled:
      Boolean(organizationId) &&
      Boolean(sessionId) &&
      enabled &&
      (queryOptions.enabled ?? true),
    queryFn: () =>
      fetchSessionAnalysis({
        organizationId: organizationId ?? "",
        sessionId: sessionId ?? "",
      }),
    queryKey,
    throwOnError: queryOptions.throwOnError ?? false,
  })

  return useRenameQueryResult(
    queryResult,
    ApiQueryName.BillPdfUploadSessionAnalysis
  )
}
